/**
 * sessionBlockVocab — the CLOSED LISTS the session-block catalog names.
 *
 * All of them are matched against, never rendered as themselves:
 *
 *  · `ACTION_HOST_OP` is `action_bar`'s closed action enum → the host op each
 *    one is wired outward to. An action that is not a key here is DROPPED by
 *    the bar rather than drawn dead (registry `action_bar.sealed`), so a bar
 *    authored against a NEWER enum still renders the buttons this build can
 *    honour instead of offering one that does nothing.
 *  · `blockGlyph` answers the preset icon set a `table` row and an
 *    `action_bar` button may name. A name the kit does not know answers
 *    `null` and the caller draws NOTHING — never a broken glyph — so an older
 *    kit degrades a row's decoration and never its data.
 *  · `fileTypeLabel` / `fileTypeGlyph` answer `file_card`'s `fileType` — an
 *    OPEN string on the wire (the server derives it from the file's extension
 *    and falls back to `"file"`), so an unknown one degrades to its own
 *    uppercased extension and the generic document glyph rather than to a hole.
 *  · `mediaProgressLabel` answers the `media_progress` op's CLOSED `stage` set.
 *    A stage this build does not know answers `null` and the caller draws the
 *    plain pending state — progress is DECORATION on top of pending, never a
 *    state of its own, and the op MAY NEVER ARRIVE at all.
 *
 * WHY IT LIVES HERE AND NOT IN `components/`: that directory is the RENDERED
 * lane. This is wire vocabulary — the same class of thing as `HOST_SLOTS` and
 * the named derives in `derives.js`, both of which sit outside the drawing
 * code for the same reason. Keeping it here means the session block
 * components hold layout and type, and not one host op name.
 */

/** `action_bar` action → the host slot it reaches. Anything else is dropped. */
export const ACTION_HOST_OP = Object.freeze({
  openSession: 'openSession',
  reloadData: 'reloadData',
  // "share this result" — the host's own share sheet, never a block-drawn one.
  macroShare: 'share',
});

/**
 * The preset glyph set. Geometric marks rather than emoji, exactly like the
 * kit's own inline set (`primitives.jsx`): a session block is monochrome, and
 * an emoji is a colour the block did not choose.
 */
const GLYPHS = {
  save: '⤓', share: '⤴', redo: '↻', link: '↗', open: '↗',
  star: '★', check: '✓', clock: '◔', info: 'ⓘ', warn: '!',
  leaf: '❧', sun: '☼', drop: '◍', flame: '▲', bag: '◗', tag: '◈',
  photo: '▣', text: '≡', grid: '⊞', chevron: '›', plus: '+',
};

/** A preset icon name → its glyph; an unknown name → null (draw nothing). */
export function blockGlyph(name) {
  const key = typeof name === 'string' ? name.trim() : '';
  return Object.prototype.hasOwnProperty.call(GLYPHS, key) ? GLYPHS[key] : null;
}

// ── file_card's `fileType` ─────────────────────────────────────────────────
//
// The wire value is the file's own extension, lowercased
// (`session_wal_reducer._file_meta_from` / `workers._artifact_file_type`), with
// `"file"` as the server's own fallback. It is NOT an enum: a worker may
// deliver anything, so both readers below are total.

/** `fileType` → the badge word. Anything else → the type itself, uppercased. */
const FILE_TYPE_LABEL = {
  pdf: 'PDF',
  doc: 'DOC', docx: 'DOCX',
  ppt: 'PPT', pptx: 'PPTX',
  xls: 'XLS', xlsx: 'XLSX',
  csv: 'CSV', txt: 'TXT', md: 'MD', rtf: 'RTF', json: 'JSON',
  zip: 'ZIP',
  file: 'FILE',
};

/** …and the monochrome glyph it wears, from the same set the blocks draw. */
const FILE_TYPE_GLYPH = {
  pdf: '▧',
  doc: '¶', docx: '¶', rtf: '¶',
  ppt: '▤', pptx: '▤',
  xls: '▦', xlsx: '▦', csv: '▦',
  txt: '≡', md: '≡', json: '≡',
  zip: '◗',
};

/** The generic document mark — what an unrecognised `fileType` wears. */
const FILE_GLYPH_DEFAULT = '❏';

const fileTypeKey = (t) => (typeof t === 'string' ? t.trim().toLowerCase() : '');

/** A `fileType`'s badge word — never empty, so the card always names itself. */
export function fileTypeLabel(type) {
  const key = fileTypeKey(type);
  if (!key) return FILE_TYPE_LABEL.file;
  return FILE_TYPE_LABEL[key] || key.slice(0, 6).toUpperCase();
}

/** A `fileType`'s glyph — an unknown one wears the generic document mark. */
export function fileTypeGlyph(type) {
  return FILE_TYPE_GLYPH[fileTypeKey(type)] || FILE_GLYPH_DEFAULT;
}

// ── the `media_progress` op's `stage` ──────────────────────────────────────
//
// CONTRACT §5: a NON-TERMINAL, slot-keyed tick the runner forwards from the
// generator's own poll. The api-server writes `progressStage` (+ `progressPct`
// / `progressLabel` when the provider sent them) onto the pending node and
// LEAVES the slot; a terminal `media_fill` clears all three. The op MAY NEVER
// ARRIVE — an image often resolves before the first tick — so its absence must
// render exactly as pending always did.

/** The closed stage set (`session_wal_reducer._MEDIA_PROGRESS_STAGES`). */
const PROGRESS_STAGE_LABEL = {
  queued: 'Queued…',
  generating: 'Generating…',
  // "uploading" is the provider's word for the last hop; the reader's word for
  // it is that the wait is nearly over.
  uploading: 'Finishing…',
};

/**
 * `stage` (+ an optional `pct`) → the line a pending plate shows, or `null` for
 * a stage this build does not know — the caller then draws plain pending.
 *
 * `pct` IS USUALLY ABSENT and is never synthesized: a percentage the generator
 * did not report is a number the kit would be making up.
 */
export function mediaProgressLabel(stage, pct) {
  return progressLabelFrom(PROGRESS_STAGE_LABEL, stage, pct);
}

/**
 * The SAME closed stage set, worded for a WORKER DELEGATE (B18 P-B).
 *
 * One difference, on purpose: a picture's last hop is the kit finishing with it,
 * but a worker's last hop is a FILE going up — "Uploading…" is what is actually
 * happening, and it is the wording the B18 spec names. Everything else is the
 * same enum, read the same way, so a stage this build does not know still
 * degrades to plain waiting rather than to a third state.
 */
const WORKER_STAGE_LABEL = {
  queued: 'Queued…',
  generating: 'Generating…',
  uploading: 'Uploading…',
};

/** `stage` (+ optional `pct`) → a worker delegate's waiting line, or null. */
export function workerProgressLabel(stage, pct) {
  return progressLabelFrom(WORKER_STAGE_LABEL, stage, pct);
}

function progressLabelFrom(map, stage, pct) {
  const key = typeof stage === 'string' ? stage.trim().toLowerCase() : '';
  const label = map[key];
  if (!label) return null;
  const n = typeof pct === 'number' && Number.isFinite(pct) ? Math.round(pct) : null;
  if (n == null || n < 0 || n > 100) return label;
  return `${label} ${n}%`;
}
